import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { toast } from "sonner";

import {
  useDeleteIntegrationSettings,
  useIntegrationSettingsQuery,
  useSaveIntegrationSettings,
  useSetIntegrationEnabled,
  useUpdateIntegrationSettings,
} from "../services/test-run";

import { useAuth } from "./useAuth";

export interface FieldDef {
  name: string;
  label: string;
  type?: "text" | "password";
  placeholder?: string;
  required?: boolean;
}

interface PluginModalContextType {
  openPluginModal: (
    pluginName: string,
    workspace: string,
    fields: FieldDef[],
    initialValues?: Record<string, any>,
    integrationId?: number,
    projectId?: number | null,
  ) => void;
  closePluginModal: () => void;
  activePlugin: string | null;
  activeWorkspaceId: string | null;
  activeProjectId: number | null;
  fields: FieldDef[];
  initialValues: Record<string, any>;
  integrationId: number | null;
  saveIntegration: (
    pluginName: string,
    values: Record<string, any>,
    projectIds?: number[],
    overrideIntegrationId?: number,
  ) => void;
  deleteIntegration: (integrationId: number) => void;
  isSaving: boolean;
  integrations: Record<string, any>[];
  getIntegration: (
    pluginName: string,
    projectId?: number | null,
  ) => Record<string, any> | undefined;
  isConfigured: (pluginName: string, projectId?: number | null) => boolean;
  toggleIntegration: (integrationId: number, enabled: boolean) => void;
}

const PluginModalContext = createContext<PluginModalContextType | undefined>(
  undefined,
);

export const PluginProvider = ({ children }: { children: ReactNode }) => {
  const [activePlugin, setActivePlugin] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(
    null,
  );
  const [activeProjectId, setActiveProjectId] = useState<number | null>(null);
  const [integrationId, setIntegrationId] = useState<number | null>(null);

  const { isAuthenticated } = useAuth();
  const deleteMutation = useDeleteIntegrationSettings();

  // global integrations state — only fetch when authenticated to avoid spurious 401s on public pages
  const integrationSettings = useIntegrationSettingsQuery({
    enabled: !!isAuthenticated,
  });
  const saveMutation = useSaveIntegrationSettings();
  const enabledMutation = useSetIntegrationEnabled();
  const updateMutation = useUpdateIntegrationSettings(integrationId!);

  const integrations = useMemo(() => {
    if (Array.isArray(integrationSettings?.data))
      return integrationSettings.data;
    return integrationSettings?.data?.data ?? [];
  }, [integrationSettings?.data]);

  const getIntegration = (name: string, projectId?: number | null) => {
    const target = String(name ?? "")
      .replace(/\s+/g, "")
      .toLowerCase();

    if (projectId != null && Number(projectId) > 0) {
      return integrations?.find((i) => {
        const current = String(i?.name ?? "")
          .replace(/\s+/g, "")
          .toLowerCase();
        const plainProjectId = Number(i?.project_id ?? i?.projectId ?? 0);
        return current === target && plainProjectId === Number(projectId);
      });
    }

    const matching =
      integrations?.filter(
        (i) =>
          String(i?.name ?? "")
            .replace(/\s+/g, "")
            .toLowerCase() === target,
      ) ?? [];
    if (target === "teams" || target === "slack") return matching[0];

    return matching.find((i) => {
      const current = String(i?.name ?? "")
        .replace(/\s+/g, "")
        .toLowerCase();
      return (
        current === target &&
        (i?.project_id == null ||
          i?.project_id === 0 ||
          i?.project_id === "0" ||
          i?.project_id === "")
      );
    });
  };

  const isConfigured = (name: string, projectId?: number | null) => {
    const integration = getIntegration(name, projectId);
    return (
      !!integration &&
      integration.is_enabled !== 0 &&
      integration.is_enabled !== false
    );
  };

  const toggleIntegration = (integrationId: number, enabled: boolean) => {
    enabledMutation.mutate(
      { integrationId, enabled },
      {
        onSuccess: () => {
          toast.success(`Integration ${enabled ? "enabled" : "disabled"}`);
          integrationSettings.refetch();
        },
        onError: (err: any) => {
          toast.error(
            err?.response?.data?.error || "Failed to update integration status",
          );
        },
      },
    );
  };

  const saveIntegration = (
    pluginName: string,
    values: Record<string, any>,
    projectIds?: number[],
    overrideIntegrationId?: number,
  ) => {
    const payloadProjectIds = projectIds?.length
      ? projectIds
      : activeProjectId != null
        ? [activeProjectId]
        : [];
    const metadata = {
      ...(values.metadata && typeof values.metadata === "object"
        ? values.metadata
        : {}),
    };

    if (values.region && String(values.region).trim()) {
      metadata.region = String(values.region).trim();
    }

    if (values.sessionToken && String(values.sessionToken).trim()) {
      metadata.sessionToken = String(values.sessionToken).trim();
    }
    
    if (values.projectArn && String(values.projectArn).trim()) {
      metadata.projectArn = String(values.projectArn).trim();
    }

    const payload = {
      name: pluginName,
      ...values,
      ...(Object.keys(metadata).length ? { metadata } : {}),
      ...(payloadProjectIds.length
        ? { projectIds: payloadProjectIds, projectId: payloadProjectIds[0] }
        : {}),
    };

    // Teams and Slack are stored as one row per project. Use the collection
    // upsert path even when an existing row opened the modal.
    const isProjectScopedMessaging = Array.isArray(values.projectConfigs);
    const targetIntegrationId = overrideIntegrationId ?? integrationId;

    // If integrationId exists, update; otherwise create
    if (targetIntegrationId && !isProjectScopedMessaging) {
      updateMutation.mutate({ ...payload, id: targetIntegrationId }, {
        onSuccess: () => {
          toast.success(`${pluginName} integration updated`);
          integrationSettings.refetch();
          closePluginModal();
        },
        onError: (err: any) => {
          toast.error(
            err?.response?.data?.error ||
              `Failed to update ${pluginName} integration`,
          );
        },
      });
    } else {
      saveMutation.mutate(payload, {
        onSuccess: () => {
          toast.success(`${pluginName} integration configured`);
          integrationSettings.refetch();
          closePluginModal();
        },
        onError: (err: any) => {
          toast.error(
            err?.response?.data?.error ||
              `Failed to configure ${pluginName} integration`,
          );
        },
      });
    }
  };

  const deleteIntegration = (integrationId: number) => {
    deleteMutation.mutate(integrationId, {
      onSuccess: () => {
        toast.success("Integration removed");
        integrationSettings.refetch();
        closePluginModal();
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.error || "Failed to remove integration",
        );
      },
    });
  };

  const openPluginModal = (
    pluginName: string,
    workspace: string,
    fields: FieldDef[],
    initialValues: Record<string, any> = {},
    integrationId: number | undefined = undefined,
    projectId: number | null = null,
  ) => {
    setActivePlugin(pluginName);
    setActiveWorkspaceId(workspace);
    setActiveProjectId(projectId);
    setFields(fields);
    setInitialValues(initialValues);
    setIntegrationId(integrationId ?? null);
  };

  const closePluginModal = () => {
    setActivePlugin(null);
    setActiveWorkspaceId(null);
    setActiveProjectId(null);
    setFields([]);
    setInitialValues({});
    setIntegrationId(null);
  };
  return (
    <PluginModalContext.Provider
      value={{
        openPluginModal,
        closePluginModal,
        activePlugin,
        activeWorkspaceId,
        activeProjectId,
        fields,
        initialValues,
        integrationId,
        integrations,
        getIntegration,
        isConfigured,
        saveIntegration,
        deleteIntegration,
        isSaving: saveMutation.isPending || updateMutation.isPending,
        toggleIntegration,
      }}
    >
      {children}
    </PluginModalContext.Provider>
  );
};

export const usePlugin = () => {
  const ctx = useContext(PluginModalContext);
  if (!ctx) throw new Error("usePlugin must be used inside Provider");
  return ctx;
};
